import {ContentBox} from '@hconnect/common/components/ContentBox'
import {shouldNotBeEmpty, ValidatorConfig, validateAll} from '@hconnect/common/validators'
import {Button, icon} from '@hm/fundamental'
import React, {FC, useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {InstructionType, DocumentUpdate, Instruction} from '../../types/documents.types'
import {
  maxStringLengthRequiredValidator,
  shouldNotBeEmptyLinkValidator,
  validToValidator,
  shouldNotBeEmptyAttachmentValidator
} from '../../validators'

import {DocumentForm} from './DocumentForm'

const emptyMap = new Map()

const documentValidatorConfig: ValidatorConfig<DocumentUpdate> = new Map([
  ['title', maxStringLengthRequiredValidator<DocumentUpdate>()],
  ['processStage', shouldNotBeEmpty<DocumentUpdate>()],
  ['validFrom', shouldNotBeEmpty<DocumentUpdate>()],
  ['validTo', validToValidator],
  ['link', shouldNotBeEmptyLinkValidator],
  ['newAttachment', shouldNotBeEmptyAttachmentValidator]
])

type State = {
  originalItem: Partial<Instruction>
  item: Partial<Instruction>
  showValidation: boolean
  validationError: Map<keyof Instruction, string>
}

type DocumentFormContainerProps = {
  plantId: string
  doClose(): void
  doSubmit(item: Instruction): void
  item?: Partial<Instruction>
}

export const DocumentFormContainer: FC<DocumentFormContainerProps> = ({
  plantId,
  item: originalItem = {type: InstructionType.PDF},
  doClose,
  doSubmit: parentDoSubmit
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [state, setState] = useState<State>({
    originalItem,
    item: originalItem,
    showValidation: false,
    validationError: emptyMap
  })

  const item = state.item
  const disableSubmit = state.showValidation && state.validationError.size > 0

  const doSubmit = () => {
    const validationError = validateAll<DocumentUpdate>(documentValidatorConfig, item)

    if (validationError.size === 0) {
      parentDoSubmit(item as Instruction)
    } else {
      setState((currentState: State) => ({
        ...currentState,
        showValidation: true,
        validationError
      }))
    }
  }

  const updateMultiPart = useCallback((delta: Partial<Instruction>) => {
    setState((currentState: State) => {
      const nextItem = {
        ...currentState.item,
        ...delta
      } as Instruction

      const validationError = currentState.showValidation
        ? validateAll(documentValidatorConfig, nextItem)
        : currentState.validationError

      return {
        ...currentState,
        item: nextItem,
        validationError
      }
    })
  }, [])

  return (
    <ContentBox data-test-id="document-form" bodyWithPadding mode="max100PercentOfParentHeight">
      <DocumentForm
        plantId={plantId}
        item={item}
        updateMultiPart={updateMultiPart}
        validationError={state.showValidation ? state.validationError : emptyMap}
      />
      <div className="mt-6 flex justify-end gap-4">
        <Button
          icon={icon.mdiClose}
          variant="tertiary"
          type="button"
          onClick={doClose}
          data-cy="document-form-cancel-button"
        >
          {t('action.cancel')}
        </Button>
        <Button
          icon={icon.mdiCheck}
          disabled={disableSubmit}
          variant="primary"
          type="button"
          onClick={doSubmit}
          data-cy="document-save-button"
        >
          {t('action.save')}
        </Button>
      </div>
    </ContentBox>
  )
}
